import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import TierBadge from "../components/TierBadge";
import ContactConsentModal from "../components/ContactConsentModal";
import { getSellerByHandle, revealContact } from "../services/sellers";
import { listReviewsForSeller } from "../services/reviews";
import { subscribeAuth } from "../services/auth";
import { needsConsent, recordConsent } from "../services/consent";
import { SEGMENTS, CONTACT_CHANNELS, RESERVE_TIERS } from "../services/constants";
import { compactNumber, formatRatePer1k, formatFlatPrice, isOnline,
  regionFlag, relativeTime } from "../utils/format";
import { buildBuyerToSellerIntent, contactHref, copyIntent } from "../utils/deeplink";
import "./Storefront.css";

export default function Storefront() {
  const { handle } = useParams();
  const [seller, setSeller] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [user, setUser] = useState(null);
  const [revealed, setRevealed] = useState({}); // { channel: value }
  const [revealError, setRevealError] = useState(null);
  // Pending reveal awaiting consent (when needsConsent() is true on click)
  const [consentPending, setConsentPending] = useState(null);

  useEffect(() => subscribeAuth(setUser), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const s = await getSellerByHandle(handle);
      if (cancelled) return;
      if (!s) { setNotFound(true); setLoading(false); return; }
      setSeller(s);
      setLoading(false);
      const rs = await listReviewsForSeller(handle);
      if (!cancelled) setReviews(rs);
    })();
    return () => { cancelled = true; };
  }, [handle]);

  if (loading) return <div className="container sf-loading">Loading…</div>;
  if (notFound) return (
    <div className="container sf-notfound">
      <h2>That seller doesn't exist.</h2>
      <p>The handle <code>@{handle}</code> isn't registered on Pop Market.</p>
      <Link to="/">Back to browse</Link>
    </div>
  );

  const online = isOnline(seller.lastActiveAt);
  const reserveLabel = RESERVE_TIERS.find((r) => r.key === seller.reserveProofTier)?.label;
  const sellerIntent = buildBuyerToSellerIntent(seller);

  const doReveal = async (channel) => {
    setRevealError(null);
    // Universal lock message — visitors don't need to know whether a
    // profile is a placeholder or a real signup. They just need the
    // sign-in path. The server still refuses to actually return
    // contact values for placeholder profiles even after auth.
    if (!user || seller._seed) {
      setRevealError("🔐 Sign in with email to reveal contact details.");
      return;
    }
    try {
      const res = await revealContact(seller.handle, channel, "seller");
      setRevealed((prev) => ({ ...prev, [channel]: res.value }));
    } catch (e) {
      console.warn("[pop-market] reveal failed", e);
      setRevealError(e.message || "Contact details aren't available right now.");
    }
  };

  const reveal = async (channel) => {
    // Skip the consent modal for anonymous viewers — the inline
    // sign-in prompt covers it. Signed-in viewers still go through
    // the 24h consent.
    if (!user || seller._seed) {
      await doReveal(channel);
      return;
    }
    if (needsConsent()) {
      setConsentPending(channel);
      return;
    }
    await doReveal(channel);
  };

  const onConsentAgree = async () => {
    recordConsent();
    const ch = consentPending;
    setConsentPending(null);
    if (ch) await doReveal(ch);
  };
  const onConsentCancel = () => setConsentPending(null);

  return (
    <div className="storefront">
      {seller.bannerUrl && (
        <div
          className="sf-banner"
          style={{ backgroundImage: `url(${seller.bannerUrl})` }}
        />
      )}
      <div className="container">
        {/* Hero */}
        <div className="sf-hero">
          <div className="sf-avatar"
            style={{ backgroundImage: seller.avatarUrl ? `url(${seller.avatarUrl})` : "none" }}>
            {!seller.avatarUrl && (seller.displayName || seller.handle)[0].toUpperCase()}
          </div>
          <div className="sf-headline">
            <h1 className="sf-name">
              {seller.displayName || seller.handle}
              <TierBadge tier={seller.tier} />
            </h1>
            <div className="sf-meta">
              <span className={`tag ${seller.game === "BGMI" ? "bgmi" : "pubgm"}`}>{seller.game}</span>
              <span>{regionFlag(seller.region, seller.countryCode)} {seller.region}</span>
              <span>·</span>
              <span>@{seller.handle}</span>
              {online ? (
                <span className="sf-online">● Online now</span>
              ) : (
                <span className="dim">Last seen {relativeTime(seller.lastActiveAt)}</span>
              )}
            </div>
            {seller.bio && <p className="sf-bio">{seller.bio}</p>}
          </div>
        </div>

        {/* Verified game-account card from Showcase */}
        {seller.gameAccountId && (
          <div className="card sf-game-card">
            <div className="sf-game-row">
              <div>
                <div className="dim sf-label">VERIFIED {seller.game} ACCOUNT</div>
                <div className="sf-game-id">
                  <span className="sf-ign">{seller.ign}</span>
                  <span className="dim"> · ID {seller.gameAccountId}</span>
                </div>
              </div>
              <div className="sf-game-stats">
                {reserveLabel && (
                  <div className="sf-stat">
                    <div className="sf-stat-num">{reserveLabel}</div>
                    <div className="dim sf-label">PROVEN RESERVES</div>
                  </div>
                )}
                {seller.reviewCount > 0 && (
                  <div className="sf-stat">
                    <div className="sf-stat-num">★ {Number(seller.avgRating || 0).toFixed(2)}</div>
                    <div className="dim sf-label">{compactNumber(seller.reviewCount)} reviews</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stock + rates dashboard */}
        <div className="sf-section-title">Stock &amp; rates</div>
        <div className="sf-segments-grid">
          {SEGMENTS.map((seg) => {
            const stock = Number(seller.stock?.[seg.key]) || 0;
            const rate = seller.rates?.[seg.key] || {};
            const isFlat = seg.key === "lumen" || seg.key === "helicopter";
            const fmt = isFlat ? formatFlatPrice : formatRatePer1k;
            const hasOffer = stock > 0 || rate.base != null;
            return (
              <div key={seg.key} className={`sf-segment-card ${hasOffer ? "" : "inactive"}`}>
                <div className="sf-segment-head">
                  <span className={`segment-pill ${seg.cssClass}`}>{seg.label}</span>
                  {!hasOffer && <span className="dim">Not offered</span>}
                </div>
                {hasOffer && (
                  <>
                    <div className="sf-stock-num">{compactNumber(stock)}<span className="dim sf-stock-unit"> in stock</span></div>
                    <div className="sf-rate-line">
                      <span className="sf-rate-num">{rate.base != null ? fmt(rate.base, seller.currency) : "DM"}</span>
                      <span className="dim"> base</span>
                    </div>
                    {rate.surge != null && (
                      <div className="sf-rate-line sf-rate-surge">
                        <span>{fmt(rate.surge, seller.currency)}</span>
                        <span className="dim"> last-{rate.surgeWindow || "24h"}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Payment methods */}
        {seller.paymentMethods?.length > 0 && (
          <>
            <div className="sf-section-title">Payment methods accepted</div>
            <div className="sf-payments">
              {seller.paymentMethods.map((p) =>
                <span key={p} className="tag">{p}</span>,
              )}
            </div>
          </>
        )}

        {/* YouTube */}
        {seller.youtubeChannelUrl && (
          <>
            <div className="sf-section-title">YouTube channel</div>
            <a href={seller.youtubeChannelUrl} target="_blank" rel="noopener noreferrer"
              className="card sf-youtube">
              <span className="sf-yt-icon">▶</span>
              <div>
                <div className="sf-yt-label">View pop-sniping videos</div>
                <div className="dim sf-yt-link">{seller.youtubeChannelUrl}</div>
              </div>
            </a>
          </>
        )}

        {/* Contact panel — buttons always visible from availableChannels.
            Click-time gating: seed → demo-locked message; anon → sign-in
            prompt; signed-in real → call revealContact and show value.
            On reveal we build a pre-filled first-message intent from the
            seller's headline data; WhatsApp deep-link carries it in
            ?text=, other channels get it via clipboard on click. */}
        <div className="sf-section-title">Contact this seller</div>
        <div className="card sf-contact">
          {CONTACT_CHANNELS
            .filter((c) => (seller.availableChannels || []).includes(c.key))
            .map((c) => {
              const value = revealed[c.key];
              const href = value ? contactHref(c.key, value, sellerIntent) : undefined;
              const onOpen = () => {
                if (c.key !== "whatsapp") copyIntent(sellerIntent);
              };
              return (
                <div key={c.key} className="sf-contact-row">
                  <span className="sf-contact-label">{c.icon} {c.label}</span>
                  {value ? (
                    <a className="sf-contact-value"
                      href={href}
                      onClick={onOpen}
                      target="_blank" rel="noopener noreferrer">
                      {value}
                    </a>
                  ) : (
                    <button className="primary sf-contact-btn"
                      onClick={() => reveal(c.key)}>
                      🔒 Show
                    </button>
                  )}
                </div>
              );
            })}
          {(seller.availableChannels || []).length === 0 && (
            <div className="dim">Seller hasn't added contact info yet.</div>
          )}
          {revealError && (
            <div className="sf-reveal-error">
              {revealError}
              {revealError.includes("Sign in") && (
                <Link to="/signin" className="sf-reveal-cta">Sign in →</Link>
              )}
            </div>
          )}
        </div>
        <div className="sf-disclaimer dim">
          PlayAware does not verify post-contact transactions or mediate
          disputes. Conduct due diligence before engaging.
        </div>

        {/* Reviews */}
        <div className="sf-section-title">
          Reviews
          {reviews.length > 0 && (
            <span className="dim sf-section-count"> · {reviews.length}</span>
          )}
        </div>
        {reviews.length === 0 ? (
          <div className="card dim">No reviews yet — be the first.</div>
        ) : (
          <div className="sf-reviews">
            {reviews.map((r) => (
              <div key={r.id} className="card sf-review">
                <div className="sf-review-head">
                  <div className="sf-review-rating">{"★".repeat(r.rating)}<span className="dim">{"★".repeat(5 - r.rating)}</span></div>
                  <div className="dim sf-review-meta">
                    {r.reviewerHandle ? `@${r.reviewerHandle}` : "Anonymous"} · {relativeTime(r.createdAt)}
                  </div>
                </div>
                {r.text && <p className="sf-review-text">{r.text}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <ContactConsentModal
        open={consentPending != null}
        sellerName={seller.displayName || `@${seller.handle}`}
        onAgree={onConsentAgree}
        onCancel={onConsentCancel}
      />
    </div>
  );
}

